import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/SectionHeading";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const CATS = ["Semua", "Touring", "Kopdar", "Modifikasi", "Baksos", "Kegiatan"];

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("Semua");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => items.filter((g) => cat === "Semua" || g.category === cat), [items, cat]);

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Navbar />
      <main className="mx-auto max-w-7xl space-y-10 px-4 pb-24 pt-32 sm:px-6 lg:px-12">
        <SectionHeading label="Dokumentasi" title="Galeri Kegiatan"
          desc="Momen touring, kopdar, dan kegiatan sosial keluarga besar 3C Chapter Jatim." />
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} data-testid={`gallery-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
              className={`rounded-md border px-3.5 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors ${
                cat === c ? "border-rose-500/50 bg-rose-500/15 text-rose-400" : "border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
              }`}>
              {c}
            </button>
          ))}
        </div>
        {loading && <p className="text-slate-500">Memuat galeri...</p>}
        {!loading && filtered.length === 0 && (
          <p data-testid="gallery-empty-state" className="rounded-xl border border-white/10 bg-[#12141C]/90 p-10 text-center text-slate-400">
            Belum ada foto pada kategori ini.
          </p>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((g, i) => (
            <motion.button key={g.id} type="button" onClick={() => setSelected(g)}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`gallery-item-${g.id}`}
              className="group relative h-48 overflow-hidden rounded-xl border border-white/10 text-left md:h-56">
              {g.media_type === "video" ? (
                <>
                  <video src={g.image_url} muted playsInline preload="metadata" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="grid h-12 w-12 place-items-center rounded-full border border-white/25 bg-black/60 backdrop-blur transition-transform duration-300 group-hover:scale-110">
                      <Play className="h-5 w-5 text-white" />
                    </span>
                  </span>
                </>
              ) : (
                <img src={g.image_url} alt={g.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              )}
            </motion.button>
          ))}
        </div>
      </main>
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-4xl border-white/10 bg-[#0B0D15] p-2" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{selected?.title}</DialogTitle>
          {selected && (
            <div>
              {selected.media_type === "video" ? (
                <video src={selected.image_url} controls autoPlay className="max-h-[75vh] w-full rounded-lg bg-black" />
              ) : (
                <img src={selected.image_url} alt={selected.title} className="max-h-[75vh] w-full rounded-lg object-contain" />
              )}
              <div className="flex items-center justify-between p-4">
                <p className="font-heading text-xl font-bold uppercase text-white">{selected.title}</p>
                <span className="rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-1 font-mono text-xs uppercase tracking-widest text-rose-400">{selected.category}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
